// How SQL Server resolves an UNQUALIFIED table name, for the MSSQL resolver's
// table lookups (alias columns, MERGE / OUTPUT / trigger targets, the value
// slot): in the database user's default schema (`SCHEMA_NAME()`, carried on
// the bus snapshot as `mssqlDefaultSchema`), then in `dbo`. When that default
// was read from the server the list is exact — a table only in some other
// schema is not what the bare name means, and two same-named tables outside
// it are never settled by list order. When the probe failed, `dbo` is only a
// guess, so the only table of that name is taken too. Same rules as the VQE's
// `lookupSnapshotRelation` (`sql-resolver/service/snapshotRelations.ts`).

export type MssqlDefaultSchemas = {
  /** Lower-cased, in resolution order. */
  readonly schemas: readonly string[];
  /** The list was read from the server (not a guess). */
  readonly exact: boolean;
};

export function mssqlDefaultSchemas(defaultSchema: string | undefined): MssqlDefaultSchemas {
  const own = defaultSchema?.trim().toLowerCase();
  if (!own) return { schemas: ['dbo'], exact: false };
  return { schemas: own === 'dbo' ? ['dbo'] : [own, 'dbo'], exact: true };
}

type NamedTable = { readonly name: string; readonly schema?: string };

/**
 * The table `schema.name` names (both already undelimited), or — with no
 * `schema` — the one SQL Server resolves the bare name to. `eq` compares two
 * identifiers (case-insensitive, like the server's default collation). A
 * table without a schema (created earlier in the buffer, `CREATE TABLE t`)
 * lives in the default schema and has no schema to be outside of.
 */
export function findMssqlTable<T extends NamedTable>(
  tables: readonly T[],
  schema: string | null,
  name: string,
  defaults: MssqlDefaultSchemas,
  eq: (a: string, b: string) => boolean = (a, b) => a.toLowerCase() === b.toLowerCase(),
): T | undefined {
  const named = tables.filter((t) => eq(t.name, name));
  if (schema) return named.find((t) => eq(t.schema ?? '', schema));
  for (const s of defaults.schemas) {
    const hit = named.find((t) => t.schema !== undefined && eq(t.schema, s));
    if (hit) return hit;
  }
  const schemaless = named.find((t) => !t.schema);
  if (schemaless) return schemaless;
  return !defaults.exact && named.length === 1 ? named[0] : undefined;
}
